import React, { FC, useCallback, useEffect, useState } from 'react';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '../../../../components/bootstrap/Modal';
import { centralApi } from '../../../AxiosInstance/CreateInstance';
import Spinner from '../../../../components/bootstrap/Spinner';
import Card, { CardBody, CardHeader, CardTitle } from '../../../../components/bootstrap/Card';
import Button from '../../../../components/bootstrap/Button';
import { Row, Col } from 'react-bootstrap';
import Icon from '../../../../components/icon/Icon';
import Progress from '../../../../components/bootstrap/Progress';
import dayjs from 'dayjs';

interface ResumeAnalysisModalProps {
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
    candidateId: string;
}

interface ResumeAnalysis {
    candidateId: string;
    candidateName: string;
    score: number;
    weightedScore: number;
    skillScore: number;
    experienceScore: number;
    educationScore: number;
    strengthScore: number;
    matchedSkills: string[];
    missingSkills: string[];
    strengths: string[];
    weaknesses: string[];
    experienceMatch: string;
    educationMatch: string;
    processingStatus: string;
    createdAt: string;
}

const ResumeAnalysisModal: FC<ResumeAnalysisModalProps> = ({
    isOpen,
    setIsOpen,
    candidateId,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [analysis, setAnalysis] =
        useState<ResumeAnalysis | null>(null);
    const toTitleCase = (str: string) =>
        str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());

    const fetchAnalysis = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            const res = await centralApi.get(
                `/candidateService/resume-analysis/${candidateId}`
            );
            setAnalysis(res.data);
        } catch (err: any) {
            setAnalysis(null);
            setError(
                err?.response?.data?.detail ||
                'Resume analysis is not available.'
            );
        } finally {
            setLoading(false);
        }
    }, [candidateId]);

    useEffect(() => {
        if (!candidateId || !isOpen) return;

        fetchAnalysis();
    }, [candidateId, isOpen, fetchAnalysis]);

    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (
            isOpen &&
            analysis &&
            analysis.processingStatus?.toLowerCase() === "processing"
        ) {
            timer = setTimeout(() => {
                fetchAnalysis();
            }, 3000);
        }

        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [analysis, isOpen, fetchAnalysis]);

    const getRecommendation = () => {
        if (!analysis) return '';

        if (analysis.weightedScore >= 8) return 'Strong Match';
        if (analysis.weightedScore >= 6) return 'Moderate Match';

        return 'Weak Match';
    };

    const scorePercentage = (analysis?.weightedScore ?? 0) * 10;

    const getScoreColor = () => {
        if (scorePercentage >= 80) return '#20c997'; // Green
        if (scorePercentage >= 60) return '#ffc107'; // Yellow
        if (scorePercentage >= 40) return '#fd7e14'; // Orange
        return '#dc3545'; // Red
    };

    const scoreColor = getScoreColor();

    const isAnalysisFailed =
        analysis?.processingStatus?.toLowerCase() === 'failed';

    const isProcessing =
        analysis?.processingStatus?.toLowerCase() === "processing";

    let modalContent = null;
    if (loading || isProcessing) {
        modalContent = (
            <div className="text-center py-5">
                <Spinner />
                <div className="mt-3 text-muted">
                    Generating resume analysis...
                </div>
            </div>
        );
    } else if (error) {
        modalContent = (
            <Card className='border-0 shadow-none'>
                <CardBody className='text-center py-5'>
                    <Icon
                        icon='Description'
                        size='5x'
                        className='text-muted mb-3'
                    />
                    <h4 className='mb-2'>No Resume Analysis Found</h4>
                    <p className='text-muted mb-0'>{error}</p>
                </CardBody>
            </Card>
        );
    } else if (isAnalysisFailed) {
        modalContent = (
            <Card className='border-0 shadow-none'>
                <CardBody className='text-center py-5'>
                    <Icon
                        icon='Error'
                        size='5x'
                        className='text-danger mb-3'
                    />
                    <h4 className='mb-2'>Resume Analysis Failed</h4>
                    <p className='text-muted mb-0'>
                        Resume could not be analyzed.
                    </p>
                </CardBody>
            </Card>
        );
    } else if (analysis) {
        modalContent = (
            <>
                {/* Candidate Header */}
                <div className='d-flex align-items-center mb-4'>
                    {/* Avatar */}
                    <div
                        className='rounded-circle bg-success text-white d-flex align-items-center justify-content-center flex-shrink-0'
                        style={{
                            width: 56,
                            height: 56,
                            fontWeight: 600,
                            fontSize: '1.25rem',
                        }}>
                        {analysis.candidateName
                            ?.split(' ')
                            ?.map((name) => name.charAt(0).toUpperCase())
                            ?.slice(0, 2)
                            ?.join('')}
                    </div>

                    {/* Candidate Details */}
                    <div className='ms-3 flex-grow-1'>
                        <div className='d-flex align-items-center'>
                            {/* <Icon icon='Person' className='me-2 icon-color' /> */}
                            <div className='fw-semibold fs-1'>
                                {toTitleCase(analysis.candidateName || '')}
                            </div>
                        </div>
                    </div>
                </div>
                <Card className='mb-4 rounded-1 bg-dark card-border'  >
                    <CardBody >
                        <div className='row align-items-center'>
                            {/* Left Side */}
                            <div className='col-md-4 text-center border-end'>
                                <div className='position-relative d-inline-block'>
                                    <svg width='120' height='120'>
                                        <circle
                                            cx='60'
                                            cy='60'
                                            r='50'
                                            stroke='#e9ecef'
                                            strokeWidth='8'
                                            fill='none'
                                        />

                                        <circle
                                            cx='60'
                                            cy='60'
                                            r='50'
                                            stroke={scoreColor}
                                            strokeWidth='8'
                                            fill='none'
                                            strokeLinecap='round'
                                            strokeDasharray={`${2 * Math.PI * 50}`}
                                            strokeDashoffset={`${2 * Math.PI * 50 * (1 - analysis.score / 10)}`}
                                            transform='rotate(-90 60 60)'
                                        />
                                    </svg>

                                    <div className='position-absolute top-50 start-50 translate-middle text-center'>
                                        <h3
                                            className='mb-0'
                                            style={{ color: scoreColor }}>
                                            {scorePercentage.toFixed(1)}%
                                        </h3>

                                        <small
                                            className='fw-semibold'
                                            style={{ color: scoreColor }}>
                                            {getRecommendation()}
                                        </small>
                                    </div>
                                </div>

                                <h6 className='mt-3 fw-bold'>
                                    Overall Match Score
                                </h6>
                            </div>

                            {/* Right Side */}
                            <div className='col-md-8'>
                                <div className='row'>
                                    <div className='col-md-6 mb-4'>
                                        <div className='info-item'>
                                            <div className='info-icon'>
                                                <Icon
                                                    icon='BarChart'
                                                    className='icon-color'
                                                />
                                            </div>

                                            <div className='info-content'>
                                                <div className='info-label'>
                                                    Processed On
                                                </div>
                                                <div className='info-value'>
                                                    {dayjs(analysis.createdAt).format('MMM D, YYYY h:mm A')}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className='col-md-6 mb-4'>
                                        <div className='info-item'>
                                            <div className='info-icon'>
                                                <Icon
                                                    icon='ConfirmationNumber'
                                                    className='icon-color'
                                                />
                                            </div>

                                            <div className='info-content'>
                                                <div className='info-label'>
                                                    Weighted Score
                                                </div>
                                                <div className='info-value'>
                                                    {analysis.weightedScore}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className='col-md-6 mb-4'>
                                        <div className='info-item'>
                                            <div className='info-icon'>
                                                <Icon
                                                    icon='CheckCircle'
                                                    className='icon-color'
                                                />
                                            </div>

                                            <div className='info-content'>
                                                <div className='info-label'>
                                                    Processing Status
                                                </div>
                                                <div className='info-value text-success'>
                                                    {toTitleCase(analysis.processingStatus)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </CardBody>
                </Card>
                {/* Score Breakdown */}
                <Row className='g-3 mb-4'>
                    <Col md={3}>
                        <Card className='rounded-1 bg-dark  card-border' >
                            <CardBody>
                                <div className='text-success'>
                                    Skill Match
                                </div>
                                <h4>
                                    {(
                                        analysis.skillScore * 10
                                    ).toFixed(0)}
                                    %
                                </h4>
                                <Progress
                                    value={analysis.skillScore * 10}
                                    min={0}
                                    max={100}
                                    color='success'
                                    height={8}
                                />
                            </CardBody>
                        </Card>
                    </Col>

                    <Col md={3}>
                        <Card className='rounded-1 bg-dark card-border' >
                            <CardBody>
                                <div className='text-primary'>
                                    Experience
                                </div>
                                <h4>
                                    {(
                                        analysis.experienceScore *
                                        10
                                    ).toFixed(0)}
                                    %
                                </h4>
                                <Progress
                                    value={analysis.experienceScore * 10}
                                    min={0}
                                    max={100}
                                    color='primary'
                                    height={8}
                                />
                            </CardBody>
                        </Card>
                    </Col>

                    <Col md={3}>
                        <Card className='rounded-1 bg-dark card-border' >
                            <CardBody>
                                <div className='text-warning'>
                                    Education
                                </div>
                                <h4>
                                    {(
                                        analysis.educationScore *
                                        10
                                    ).toFixed(0)}
                                    %
                                </h4>
                                <Progress
                                    value={analysis.educationScore * 10}
                                    min={0}
                                    max={100}
                                    color='warning'
                                    height={8}
                                />
                            </CardBody>
                        </Card>
                    </Col>

                    <Col md={3}>
                        <Card className='rounded-1 bg-dark  card-border ' >
                            <CardBody >
                                <div className='text-info'>
                                    Strength
                                </div>
                                <h4>
                                    {(
                                        analysis.strengthScore *
                                        10
                                    ).toFixed(0)}
                                    %
                                </h4>
                                <Progress
                                    value={analysis.strengthScore * 10}
                                    min={0}
                                    max={100}
                                    color='info'
                                    height={8}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Row>
                    <Col md={6} className='d-flex'>
                        <Card className='mb-3 rounded-1 bg-dark flex-fill card-border' >
                            <CardHeader>
                                <CardTitle className='d-flex align-items-center gap-2'>
                                    <Icon
                                        icon='CheckCircle'
                                        className='icon-color'
                                    />
                                    Matched Skills ({analysis.matchedSkills?.length || 0})
                                </CardTitle>
                            </CardHeader>

                            <CardBody>
                                <ul className='green-bullets'>
                                    {analysis.matchedSkills.map(
                                        (skill) => (
                                            <li key={skill}>
                                                {skill}
                                            </li>
                                        ),
                                    )}
                                </ul>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col md={6} className='d-flex'>
                        <Card className='mb-3 rounded-1 bg-dark flex-fill card-border ' >
                            <CardHeader>
                                <CardTitle className='d-flex align-items-center gap-2'>
                                    <Icon
                                        icon='Cancel'
                                        className='text-danger'
                                    />
                                    Missing Skills ({analysis.missingSkills?.length || 0})
                                </CardTitle>
                            </CardHeader>

                            <CardBody>
                                <ul className='red-bullets'>
                                    {analysis.missingSkills.map(
                                        (skill) => (
                                            <li key={skill}>
                                                {skill}
                                            </li>
                                        ),
                                    )}
                                </ul>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Card className='mb-3 rounded-1 bg-dark card-border' >
                    <CardHeader>
                        <CardTitle className='d-flex align-items-center gap-2'>
                            <Icon
                                icon='Star'
                                className='text-success'
                            />
                            Strengths ({analysis.strengths?.length || 0})
                        </CardTitle>
                    </CardHeader>

                    <CardBody>
                        <ul className='green-bullets'>
                            {analysis.strengths.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </ul>
                    </CardBody>
                </Card>

                <Card className='mb-3 rounded-1 bg-dark card-border' >
                    <CardHeader>
                        <CardTitle className='d-flex align-items-center gap-2'>
                            <Icon
                                icon='Cancel'
                                className='text-warning'
                            />
                            Weaknesses ({analysis.weaknesses?.length || 0})
                        </CardTitle>
                    </CardHeader>

                    <CardBody>
                        <ul className='yellow-bullets'>
                            {analysis.weaknesses.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </ul>
                    </CardBody>
                </Card>

                <Row>
                    <Col md={6} className="d-flex">
                        <Card className="rounded-1 flex-fill bg-dark card-border" >
                            <CardHeader>
                                <CardTitle className="d-flex align-items-center gap-2">
                                    <Icon icon="Work" className="text-info" />
                                    Experience Match
                                </CardTitle>
                            </CardHeader>

                            <CardBody>
                                {analysis.experienceMatch}
                            </CardBody>
                        </Card>
                    </Col>

                    <Col md={6} className="d-flex">
                        <Card className="rounded-1 flex-fill bg-dark card-border" >
                            <CardHeader>
                                <CardTitle className="d-flex align-items-center gap-2">
                                    <Icon icon="School" className="text-primary" />
                                    Education Match
                                </CardTitle>
                            </CardHeader>

                            <CardBody>
                                {analysis.educationMatch}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </>
        );
    }

    return (
        <Modal
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            size='xl'>
            <ModalHeader setIsOpen={setIsOpen}>
                <ModalTitle id='resume anlysis'>Resume Analysis</ModalTitle>
            </ModalHeader>

            <ModalBody>
                {modalContent}
            </ModalBody>

            <ModalFooter>
                <Button
                    className=' hrms-gradient-btn hrms-btn'
                    onClick={() => setIsOpen(false)}>
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ResumeAnalysisModal; 
